export const ACCESS_TOKEN_KEY = "cehr_access_token";
export const REFRESH_TOKEN_KEY = "cehr_refresh_token";
export const ORGANIZATION_KEY = "cehr_organization";
export const LOGIN_EMAIL_KEY = "cehr_login_email";

export const getApiBaseUrl = () => (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

export const getAssetBaseUrl = () => {
  const configuredAssetBaseUrl = (process.env.REACT_APP_ASSET_BASE_URL || "").replace(/\/+$/, "");

  if (configuredAssetBaseUrl) {
    return configuredAssetBaseUrl;
  }

  const apiBaseUrl = getApiBaseUrl();

  if (!apiBaseUrl) {
    return "";
  }

  try {
    return new URL(apiBaseUrl).origin;
  } catch {
    return apiBaseUrl.replace(/\/api$/i, "");
  }
};

export const buildAssetUrl = (assetPath) => {
  if (!assetPath) {
    return "";
  }

  const normalizedAssetPath = `${assetPath}`.trim().replace(/\\/g, "/");

  if (/^https?:\/\//i.test(normalizedAssetPath)) {
    return encodeURI(normalizedAssetPath);
  }

  const baseUrl = getAssetBaseUrl();
  const normalizedPath = normalizedAssetPath.startsWith("/") ? normalizedAssetPath : `/${normalizedAssetPath}`;

  return encodeURI(baseUrl ? `${baseUrl}${normalizedPath}` : normalizedPath);
};

export const getStoredAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

export const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const getStoredLoginEmail = () => localStorage.getItem(LOGIN_EMAIL_KEY);

const normalizeEmail = (value) => `${value || ""}`.trim().toLowerCase();

const getOrganizationEmails = (organization) =>
  [
    organization?.email,
    organization?.organizationEmail,
    organization?.companyEmail,
    organization?.accountEmail,
  ]
    .map(normalizeEmail)
    .filter(Boolean);

export const isCompanyAccount = (organization) => {
  const storedLoginEmail = normalizeEmail(getStoredLoginEmail());
  const organizationEmails = getOrganizationEmails(organization);

  // When we know which email signed in, trust that against known org emails.
  if (storedLoginEmail) {
    return organizationEmails.includes(storedLoginEmail);
  }

  const accountType = `${organization?.accountType || organization?.type || ""}`
    .trim()
    .toLowerCase();

  if (["company", "organization"].includes(accountType)) {
    return true;
  }

  return false;
};

export const resolveDashboardPath = (organization) =>
  isCompanyAccount(organization) ? "/dashboard" : "/employee-dashboard";

export const resolveContractLandingPath = (modulePermissions = []) => {
  const destinations = [
    ["productivity", "/productivity"], ["dashboard", "/employee-dashboard"],
    ["contract-staff", "/contract-staff"], ["employees", "/employee"],
    ["chats", "/chat"], ["attendance", "/attendance"],
    ["engagement", "/engagement"], ["requests", "/requests"],
    ["documents", "/resources"], ["payroll", "/payroll"],
    ["e-wallet", "/e-wallet"], ["surveys", "/surveys"],
    ["query-management", "/query-management"], ["conflict-resolution", "/conflict-resolution"],
    ["performance-evaluation", "/performance-evaluation"], ["job-descriptions", "/job-descriptions"],
    ["reporting", "/reporting"],
    ["company-settings", "/company-settings"], ["profile", "/profile"],
  ];
  return destinations.find(([moduleId]) => modulePermissions.includes(moduleId))?.[1] || "/contract-access";
};

export const getStoredOrganization = () => {
  const storedOrganization = localStorage.getItem(ORGANIZATION_KEY);

  if (!storedOrganization) {
    return null;
  }

  try {
    return JSON.parse(storedOrganization);
  } catch {
    return null;
  }
};

export const setStoredSession = ({ accessToken, refreshToken, organization, loginEmail }) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  if (organization) {
    localStorage.setItem(ORGANIZATION_KEY, JSON.stringify(organization));
  }

  if (loginEmail) {
    localStorage.setItem(LOGIN_EMAIL_KEY, `${loginEmail}`.trim().toLowerCase());
  }
};

export const clearStoredSession = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ORGANIZATION_KEY);
  localStorage.removeItem(LOGIN_EMAIL_KEY);
};

export const hasStoredSession = () => !!getStoredAccessToken() || !!getStoredRefreshToken();
